import { FC, ReactNode, HTMLAttributes } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}

interface CardHeaderProps {
  title?: ReactNode;
  description?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}

interface CardBodyProps {
  children: ReactNode;
  className?: string;
}

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export const Card: FC<CardProps> & {
  Header: FC<CardHeaderProps>;
  Body: FC<CardBodyProps>;
  Footer: FC<CardFooterProps>;
} = ({ children, className = '', ...props }) => {
  return (
    <div
      className={`shadow-xs overflow-hidden rounded-2xl border border-surface-border-light bg-surface-panel-light transition-colors duration-150 dark:border-surface-border-dark dark:bg-surface-panel-dark ${className} `}
      {...props}
    >
      {children}
    </div>
  );
};

Card.Header = ({
  title,
  description,
  subtitle,
  action,
  actions,
  children,
  className = '',
}) => {
  const resolvedDesc = description || subtitle;
  const resolvedAction = action || actions;
  return (
    <div
      className={`flex items-center justify-between border-b border-surface-border-light px-5 py-4 dark:border-surface-border-dark ${className} `}
    >
      {children ? (
        children
      ) : (
        <>
          <div className="min-w-0 flex-1">
            {title && (
              <h3 className="text-sm font-semibold text-content-main-light dark:text-content-main-dark">
                {title}
              </h3>
            )}
            {resolvedDesc && (
              <p className="mt-0.5 text-xs text-content-muted-light dark:text-content-muted-dark">
                {resolvedDesc}
              </p>
            )}
          </div>
          {resolvedAction && (
            <div className="ml-4 flex-shrink-0">{resolvedAction}</div>
          )}
        </>
      )}
    </div>
  );
};

Card.Body = ({ children, className = '' }) => {
  return (
    <div
      className={`p-5 text-content-main-light dark:text-content-main-dark ${className}`}
    >
      {children}
    </div>
  );
};

Card.Footer = ({ children, className = '' }) => {
  return (
    <div
      className={`flex items-center justify-between border-t border-surface-border-light bg-surface-subtle-light px-5 py-3 dark:border-surface-border-dark dark:bg-surface-subtle-dark ${className} `}
    >
      {children}
    </div>
  );
};
